import HtmlEditor, { Toolbar, Item, ImageUpload, MediaResizing } from 'devextreme-react/html-editor';
import 'devextreme/ui/drop_down_button';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

export interface EmTplHtmlEditorHandle {
  flush: () => void; //최신 값을 부모에게 반영
  getValue: () => string; // 최신 값을 바로 가져옴
}

export interface EmTplHtmlEditorProps {
  defaultValue: string;
  readOnly?: boolean;
  onCommit?: (html: string) => void; // 에디터 영역에서 focus 벗어날때 호출
  width?: number | string;
  height?: number | string;
  className?: string;
  showToolbar?: boolean;
  mergeFields?: string[];
  mergeFieldPrefix?: string; // default '«'
  mergeFieldSuffix?: string; // default '»'
}

const FILE_UPLOADER_OPTIONS = {
  allowedFileExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.bmp'],
};


const EmTplHtmlEditor = React.memo(
  React.forwardRef<EmTplHtmlEditorHandle, EmTplHtmlEditorProps>(
    function EmTplHtmlEditor(
      {
        defaultValue,
        readOnly = false,
        onCommit,
        width = 860,
        height = 400,
        className,
        showToolbar = true,
        mergeFields,
        mergeFieldPrefix = '«',
        mergeFieldSuffix = '»',
      },
      ref
    ) {
      const editorRootRef = useRef<HTMLDivElement>(null);
      const htmlEditorRef = useRef<HtmlEditor>(null);
      const dummyInputRef = useRef<HTMLInputElement>(null);
      const currentValueRef = useRef(defaultValue);
      const onCommitRef = useRef(onCommit ?? (() => {}));
      onCommitRef.current = onCommit ?? (() => {});

      /*
      * 에디터 영역 바깥을 클릭시 dummy input으로 강제로 focus를 이동 시킴
      * 이 동작을 하지 않으면 에디터 영역 바깥을 클릭했음에도 에디터 영역에 커서가 계속 표시됨
      */
      useEffect(() => {
        function handleDocumentMouseDown(e: MouseEvent) {
          const editorInstance = htmlEditorRef.current?.instance;
          if (!editorInstance) return;

          // 에디터 전체 영역
          const containerEl = editorInstance.element() as HTMLElement | null;
          if (!containerEl) return;

          const target = e.target as Node;
          if (containerEl.contains(target)) return; 

          const activeEl = document.activeElement;
          if (activeEl && containerEl.contains(activeEl)) {
            e.preventDefault();
            dummyInputRef.current?.focus(); 
          }
        }

        document.addEventListener('mousedown', handleDocumentMouseDown, true);
        return () => {
          document.removeEventListener('mousedown', handleDocumentMouseDown, true);
        };
      }, []);

      useImperativeHandle(
        ref,
        () => ({
          flush: () => {
            onCommitRef.current(currentValueRef.current);
          },
          getValue: () => currentValueRef.current,
        }),
        []
      );

      return (
        <div
          ref={editorRootRef}
          style={{
            width,
            flex: '0 0 auto',
            alignSelf: 'flex-start',
          }}
        >
          <input
            ref={dummyInputRef}
            type="text"
            tabIndex={-1}
            readOnly
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: 1,
              height: 1,
              opacity: 0,
              border: 'none',
              padding: 0,
              pointerEvents: 'none',
            }}
          />
          <HtmlEditor
            ref={htmlEditorRef}
            width={width}
            height={height}
            className={['emtpl-html-editor', className].filter(Boolean).join(' ')}
            defaultValue={defaultValue}
            readOnly={readOnly}
            onValueChanged={(e) => {
              currentValueRef.current = e.value;
            }}
          >
            {showToolbar && (
              <Toolbar multiline={true}>
                <Item name="undo" />
                <Item name="redo" />
                <Item name="separator" />
                <Item name="bold" />
                <Item name="italic" />
                <Item name="underline" />
                <Item name="strike" />
                <Item name="color" />
                <Item name="background" />
                <Item name="separator" />
                <Item name="alignLeft" />
                <Item name="alignCenter" />
                <Item name="alignRight" />
                <Item name="alignJustify" />
                <Item name="separator" />
                <Item name="orderedList" />
                <Item name="bulletList" />
                <Item name="separator" />
                <Item name="link" />
                <Item name="image" />
                {mergeFields && mergeFields.length > 0 && <Item name="separator" />}
                {mergeFields && mergeFields.length > 0 && (
                  <Item
                    widget="dxDropDownButton"
                    options={{
                      text: 'Insert Merge Field',
                      width: 160,
                      height: 28,
                      stylingMode: 'text',
                      items: mergeFields,
                      dropDownOptions: { maxHeight: 300 },
                      onItemClick: (e: any) => {
                        const instance = htmlEditorRef.current?.instance;
                        if (!instance) return;
                        const selection = instance.getSelection() || {
                          index: instance.getLength(),
                          length: 0,
                        };
                        instance.insertText(selection.index, `${mergeFieldPrefix}${e.itemData}${mergeFieldSuffix}`, {});
                      },
                    }}
                  />
                )}
              </Toolbar>
            )}

            {showToolbar && (
              <ImageUpload
                tabs={['file']}
                fileUploadMode="base64"
                fileUploaderOptions={FILE_UPLOADER_OPTIONS}
              />
            )}
            {showToolbar && <MediaResizing enabled={true} />}
          </HtmlEditor>
        </div>
      );
    }
  )
);

export default EmTplHtmlEditor;
